// Shared CSV primitives for the bulk-import flows (products, ingredients,
// recipes). Extracted from BulkAddModal so RecipeImportModal parses files
// exactly the same way.

// Split one CSV line, honouring double-quoted fields that contain commas.
// A doubled quote ("") inside a quoted field is a literal quote.
const splitLine = (line) => {
    const out = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') { field += '"'; i++; }
            else inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
            out.push(field);
            field = '';
        } else {
            field += ch;
        }
    }
    out.push(field);
    return out.map(f => f.trim());
};

// Header keys as BulkAddModal expects them: "Product Name" -> "product_name".
export const normaliseHeader = (h) =>
    h.trim().toLowerCase().replace(/\s+/g, '_');

/**
 * Parse CSV text into { headers, rows }.
 *
 * Headers are preserved verbatim by default — the recipe endpoints match on
 * the exact column names. Pass { normaliseHeaders: true } for the snake_case
 * keys BulkAddModal's column configs are written against.
 */
export const parseCsv = (text, { normaliseHeaders = false } = {}) => {
    const lines = String(text || '')
        .split(/\r?\n/)
        .filter(l => l.trim() !== '');
    if (lines.length === 0) return { headers: [], rows: [] };

    const raw = splitLine(lines[0]);
    const headers = normaliseHeaders ? raw.map(normaliseHeader) : raw;

    const rows = lines.slice(1).map((line) => {
        const values = splitLine(line);
        const row = {};
        headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
        return row;
    });
    return { headers, rows };
};

// Characters that make a spreadsheet treat a cell as a formula rather than
// text. CSV quoting does NOT stop this — Excel/Sheets strip the quoting before
// parsing the cell, so `"=HYPERLINK(...)"` still evaluates.
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

// A plain number is never a formula, and '-' is both a trigger character and
// the start of every negative figure. Without this exemption a cash difference
// of -5.00 would export as text and stop summing — breaking the file for the
// people it is built for.
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

// Product, category and ingredient names are staff-entered and land in the
// first column of an export. A product named `=HYPERLINK("http://evil",…)`
// would run when someone opens the file. The leading apostrophe makes the
// spreadsheet treat the value as literal text and is not itself displayed.
const neutraliseFormula = (str) =>
    (!PLAIN_NUMBER.test(str) && FORMULA_TRIGGERS.includes(str.charAt(0)))
        ? `'${str}`
        : str;

// Exported: the EOD report export (pages/portal/reporting/eod/eodFormats.js)
// escapes cells one at a time rather than through rowsToCsv, because its file
// is sectioned — several header rows with differing column counts — so it
// cannot go through a single headers/rows pair.
export const escapeField = (value) => {
    const str = value === null || value === undefined ? '' : String(value);
    const safe = neutraliseFormula(str);
    // \r included alongside \n: a lone carriage return inside a field breaks
    // the row just as badly as a newline.
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const rowsToCsv = (headers, rows) => {
    const head = headers.map(escapeField).join(',');
    const body = (rows || []).map(
        r => headers.map(h => escapeField(r[h])).join(',')
    );
    return [head, ...body].join('\n');
};

export const downloadCsvContent = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename, headers, rows) =>
    downloadCsvContent(rowsToCsv(headers, rows), filename);
